import React, { useEffect, useState } from 'react'
import { parseMetadata } from '../utils'
import { Button } from '../../../../../components/Button/Button'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { selectSelectedMeetingTemplate, selectSessionMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { useUpdateRoomSessionMetadata } from '../hooks/api'
import { showToast } from '../../../../../store/meetingSummary/display'
import './MeetingTemplateInfo.scss'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomTrackingDetails } from '../../tracking'

export interface MeetingTemplateInfoProps {}

export const MeetingTemplateInfo: React.FC<MeetingTemplateInfoProps> = () => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const roomSession = useAppSelector(selectRoomSession)
  const sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)

  const updateRoomSessionMetadata = useUpdateRoomSessionMetadata()

  const [dismissed, setDismissed] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (selectedMeetingTemplate && sessionMeetingTemplate && selectedMeetingTemplate.id !== sessionMeetingTemplate.id) {
      setLoading(false)
      setDismissed(false)
    }
  }, [selectedMeetingTemplate, sessionMeetingTemplate])

  if (dismissed || !selectedMeetingTemplate || !sessionMeetingTemplate || sessionMeetingTemplate.id === selectedMeetingTemplate.id) {
    return null
  }

  const _handleSaveMeetingTemplate = async () => {
    if (!roomSession) {
      return
    }

    setLoading(true)
    const currentMetadata = parseMetadata(roomSession.metadata)
    const { external } = currentMetadata
    await updateRoomSessionMetadata({ ...external, meetingTemplateId: selectedMeetingTemplate.id })
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'The meeting type has been changed.' }))
    setDismissed(true)
  }

  const handleSaveMeetingTemplate = tracked(_handleSaveMeetingTemplate, {
    eventName: TrackEvent.ROOM_SESSION_MEETING_TEMPLATE_CHANGED,
    eventDetails: { ...getRoomTrackingDetails(roomSession), newMeetingTemplateId: selectedMeetingTemplate.id },
  })

  return (
    <div className='w-100 position-absolute py-2 zocks-px-1.75 d-flex align-items-center justify-content-between zocks-bg-color-primary-light meeting-template-info-container'>
      <div className='d-flex gap-2 align-items-center'>
        <div className='d-flex gap-2 align-items-center'>
          <i className='bi bi-exclamation-circle' />
          <div className='paragraph-medium'>
            The meeting type has changed. Do you want `<span className='bold'>{selectedMeetingTemplate.name}`</span> to be the new meeting type for this
            meeting?
          </div>
        </div>
        <ScopedBlock
          scope={{
            scope: 'room_session:update',
            superScope: ['room_session:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Button variant='primary' className='button-small p-1' onClick={handleSaveMeetingTemplate} loading={loading}>
            Save
          </Button>
        </ScopedBlock>
      </div>
      <div>
        <Button variant='borderless' className='zocks-bg-color-primary-light' onClick={() => setDismissed(true)}>
          <i className='bi bi-x' />
        </Button>
      </div>
    </div>
  )
}
